import { useComputed, useSignal } from '@preact/signals'
import { createRef } from 'preact'
import { useEffect } from 'preact/hooks'
import { useBreedCount } from '@/composables/breedCount'
import { stripParentheses } from '@/utils/stripParentheses'

interface Breed {
  id: number
  name: string
  image?: string
}

interface Props {
  breeds: Array<Breed>
}

const MIN_VISIBLE_DOG_COUNT = 100

const LOCALSTORAGE_KEY = 'Lyka::BreedForm'

const getSavedBreedId = (): number | undefined => {
  const value = window.localStorage?.getItem(LOCALSTORAGE_KEY) ?? undefined

  if (value) {
    return Number.parseInt(value)
  }

  return undefined
}

const saveBreedId = (breed: Breed | undefined): void => {
  if (breed) {
    localStorage.setItem(LOCALSTORAGE_KEY, String(breed.id))
  }
}

const getBreedById = (breeds: Breed[], id: number | undefined): Breed | undefined => {
  return breeds.find((b) => b.id === id)
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export default function HomeBannerBreedForm({ breeds }: Props) {
  const expanded = useSignal(false)
  const breed = useSignal<Breed | undefined>()
  const breedCount = useSignal<number>(0)
  const text = useSignal<string>('')
  const input = createRef<HTMLInputElement>()

  const showDogCount = useComputed(() => breedCount.value >= MIN_VISIBLE_DOG_COUNT)
  const formattedBreedName = useComputed(() => (breed.value ? stripParentheses(breed.value.name) : undefined))

  const open = (): void => {
    expanded.value = true
  }

  const close = (): void => {
    expanded.value = false
  }

  const visibleBreeds = useComputed<Breed[]>(() => {
    const lower = text.value.toLowerCase()

    const filtered = breeds.filter((b) => {
      return b.name.toLowerCase().includes(lower)
    })

    const sorted = filtered.sort((a, b) => {
      return a.name.toLowerCase().indexOf(lower) - b.name.toLowerCase().indexOf(lower)
    })

    return sorted
  })

  const handleInput = (event: Event): void => {
    if (event.target instanceof HTMLInputElement) {
      text.value = event.target.value
    }

    breedCount.value = 0
    breed.value = undefined

    open()
  }

  const unselectBreed = (): void => {
    breed.value = undefined
    breedCount.value = 0

    if (input.current) {
      input.current.value = ''
    }

    close()
  }

  const handleBreedSelect = async (selected: Breed): Promise<void> => {
    breed.value = selected
    breedCount.value = 0

    if (input.current) {
      input.current.value = selected.name
    }

    const count = await useBreedCount().query(selected.id)

    if (count) {
      breedCount.value = count
    }

    close()
  }

  const handleSubmit = (event: Event): void => {
    event.preventDefault()

    const url = new URL('/get-started', window.location.origin)

    if (breed.value) {
      url.searchParams.set('breed', String(breed.value.id))
    }

    location.href = url.toString()
  }

  const handleBlur = (): void => {
    // On blur attempt to find a breed match and select it
    if (breed.value === undefined) {
      // Get trimmed input value
      const trimmed = text.value.toLowerCase().trim()

      if (trimmed) {
        // Find a breed that matches the trimmed input
        const match = breeds.find((b) => b.name.toLowerCase() === trimmed)

        // If a match is found, select it
        if (match) {
          handleBreedSelect(match)
        }
      }
    }

    // Delay closing the dropdown to allow click events to register before hiding
    setTimeout(() => close(), 100)
  }

  const handleFocus = (): void => {
    open()
  }

  useEffect(() => {
    const saved = getBreedById(breeds, getSavedBreedId())

    if (saved) {
      handleBreedSelect(saved)
    }
  }, [])

  useEffect(() => {
    saveBreedId(breed.value)
  }, [breed.value])

  return (
    <form class="breed-form-field" onSubmit={handleSubmit} aria-label="breed-form">
      <div class="tw-flex tw-bg-white tw-rounded-full tw-border tw-border-green-dark tw-justify-between tw-min-w-0">
        <input
          type="text"
          ref={input}
          name="breed"
          id="breed"
          class="breed-form-input"
          placeholder="Eg. Labrador"
          autocomplete="off"
          spellcheck={false}
          onInput={handleInput}
          onBlur={handleBlur}
          onFocus={handleFocus}
          role="combobox"
        />

        <div class="breed-form-list" hidden={!expanded.value} role="listbox">
          <ul class="breed-options">
            <li
              tabindex={-1}
              data-breed-id="-1"
              data-breed="Mixed Breed"
              class="breed-form-option breed-form-option-mixed"
              role="option"
              onClick={unselectBreed}
            >
              Mixed Breed
            </li>
            {visibleBreeds.value.map((breed) => (
              <li
                tabindex={-1}
                class="breed-form-option"
                role="option"
                onClick={(): unknown => handleBreedSelect(breed)}
              >
                <BreedOptionLabel breed={breed} text={text.value} />
              </li>
            ))}
            <li class="breed-form-option-other" hidden tabindex={-1} role="option" onClick={unselectBreed}>
              Other
            </li>
          </ul>
        </div>
        <div>
          <button type="submit" class="lyka-btn lyka-btn-md lyka-btn-dark">
            Get my plan
          </button>
        </div>
      </div>

      {breed.value && (
        <div class="breed-form-tooltip tw-animate-slide-up">
          {breed.value.image && (
            <img
              src={breed.value.image}
              class="breed-form-tooltip-image tw-rounded-lg tw-h-16 tw-w-16 tw-object-cover"
            />
          )}
          <div class="breed-form-tooltip-description">
            {breed && showDogCount.value ? (
              <>
                Join <strong> {breedCount.toLocaleString()}</strong> {formattedBreedName.value}s who already love Lyka.
              </>
            ) : (
              <>Good news! Lyka is ideal for {formattedBreedName.value}s of all ages.</>
            )}
          </div>
        </div>
      )}
    </form>
  )
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function BreedOptionLabel({ breed, text }: { breed: Breed; text: string }) {
  const start = breed.name.toLowerCase().indexOf(text.toLowerCase())
  const end = start + text.length

  const pre = breed.name.slice(0, start)
  const highlighted = breed.name.slice(start, end)
  const post = breed.name.slice(end)

  return (
    <span class="breed-option-label">
      {pre}
      <span class="tw-text-green">{highlighted}</span>
      {post}
    </span>
  )
}
